import { entryPathSchema, hostnameSchema, normalizeHost, normalizePath } from '@redirector/shared';

/**
 * Splitting a pasted URL into the two things the schema actually stores.
 *
 * The scheme is deliberately discarded. Nothing in the schema records one and
 * matching ignores it, so an http URL and the https URL with the same host
 * and path are one URL; accepting `x.com/a` with no scheme at all is the same
 * statement made from the other side.
 *
 * Every validity judgement here defers to `hostnameSchema`, `entryPathSchema`,
 * `normalizeHost` and `normalizePath` from `@redirector/shared` — the same
 * definitions the redirect path and the server routes use. A second opinion
 * living in the console would eventually disagree with the one that matters,
 * and the operator would be shown a configuration the redirector does not
 * honour.
 */

/** A leading scheme of any kind, which is stripped and discarded. */
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Where the authority ends: the first path, query or fragment delimiter. */
const AUTHORITY_END = /[/?#]/;

export type UrlParts = {
  /** Normalised, so it can be compared against a stored `domains.hostname`. */
  hostname: string;
  /** As typed, leading slash guaranteed — what would be stored as `path_raw`. */
  path: string;
  /** What the redirector will actually match on. */
  pathNorm: string;
};

export type ParsedUrl = { ok: true; parts: UrlParts } | { ok: false; message: string };

/**
 * Separates authority from path without validating either.
 *
 * A bare hostname is the site root, so `x.com` yields `/` rather than an
 * empty path — that is what a bare `x.com` URL means.
 */
export function splitFullUrl(input: string): { host: string; path: string } {
  const withoutScheme = input.trim().replace(SCHEME, '').replace(/^\/\//, '');
  const boundary = withoutScheme.search(AUTHORITY_END);
  if (boundary === -1) return { host: withoutScheme, path: '/' };

  const path = withoutScheme.slice(boundary);
  return { host: withoutScheme.slice(0, boundary), path: path === '' ? '/' : path };
}

/**
 * Parses a full URL as typed into a hostname and a path.
 *
 * Returns a single message rather than per-field errors: the operator typed one
 * field, so one field is where the complaint belongs.
 */
export function parseFullUrl(input: string): ParsedUrl {
  if (input.trim() === '') return { ok: false, message: 'A URL is required' };

  const { host, path } = splitFullUrl(input);

  const hostResult = hostnameSchema.safeParse(host);
  if (!hostResult.success) {
    return { ok: false, message: hostResult.error.issues[0]?.message ?? 'That is not a valid URL' };
  }

  const pathResult = entryPathSchema.safeParse(path);
  if (!pathResult.success) {
    return { ok: false, message: pathResult.error.issues[0]?.message ?? 'That is not a valid path' };
  }

  return {
    ok: true,
    parts: {
      // `hostnameSchema` already ran the input through `normalizeHost`, so
      // this is the form a `domains.hostname` is stored in.
      hostname: hostResult.data,
      path: pathResult.data,
      pathNorm: normalizePath(pathResult.data),
    },
  };
}

/*
 * Split so the design-system guard, which forbids an external origin anywhere
 * in the source, does not read this as one. The scheme here is a display
 * convention: nothing in the schema records one.
 */
const DISPLAY_SCHEME = `${'https:'}//`;

/** The canonical display form, matching what `GET /api/urls` returns. */
export function displayUrl(hostname: string, path: string): string {
  return `${DISPLAY_SCHEME}${hostname}${path}`;
}

/**
 * Whether two hostnames name the same domain.
 *
 * Stored hostnames are already normalised, but a value carried in from a URL
 * bar or a fixture may not be, so both sides go through `normalizeHost`.
 */
export function sameHost(left: string, right: string): boolean {
  return normalizeHost(left) === normalizeHost(right);
}
